import { createContext, useCallback, useState } from 'react';
import useUnities from '../../hooks/useUnities';
import useCities from '../../hooks/useCities';
import config from '../../../../../../config/config';
import { getUserSession } from '../../../../../../util/core/Session';
import { chooseProfileFilter } from '../../../../../../helpers/chooseProfileFilter';

export const CIHAFiltersContext = createContext();

const currentYear = new Date().getFullYear().toString();
const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');

export const cihaFiltersInitialState = {
    cidade: {
        value: '',
        label: 'Todas',
        governance_room_client_city_id: null,
        governance_room_geo_city_id: null
    },
    unidade: {
        value: '',
        label: 'Todas',
        governance_room_client_unity_id: null,
        governance_room_geo_unity_id: null
    },
    year: { value: currentYear, label: currentYear },
    month: { value: '', label: 'Todos' },
    location: ''
};

const monthNames = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Maio',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez'
];

const CIHAFiltersProvider = ({ children, initialState }) => {
    const session = getUserSession();
    const profile = session.user.application_profile_id;
    const isAdm = profile === config.default_foreign_key.profiles.zellusAdmin;

    const [filters, setFilters] = useState(() => {
        const state = initialState || cihaFiltersInitialState;

        const savedUnity = localStorage.getItem('dashboard_unity');
        const withUnity = savedUnity
            ? { ...state, unidade: JSON.parse(savedUnity) }
            : state;

        if (isAdm) {
            const savedCity = localStorage.getItem('dashboard_city');
            return savedCity
                ? { ...withUnity, cidade: JSON.parse(savedCity) }
                : withUnity;
        }

        const sessionCityId = chooseProfileFilter().governance_room_client_city_id || null;

        return {
            ...withUnity,
            cidade: {
                value: sessionCityId,
                label: '',
                governance_room_client_city_id: sessionCityId,
                governance_room_geo_city_id: null
            }
        };
    });

    const { data: cities } = useCities(isAdm);

    const resolvedCidade = filters.cidade.value
        ? filters.cidade
        : (isAdm && cities && cities.length > 0 ? cities[0] : filters.cidade);

    const { data: unities } = useUnities(resolvedCidade.governance_room_client_city_id);

    const resolvedUnidade = filters.unidade.value
        ? filters.unidade
        : (unities && unities.length > 0 ? unities[0] : filters.unidade);

    const resolvedFilters = { ...filters, cidade: resolvedCidade, unidade: resolvedUnidade };

    const handleChangeFilters = (values) => {
        setFilters(values);
    };

    const resetFilters = () => {
        setFilters((previous) => ({
            ...(initialState || cihaFiltersInitialState),
            cidade: previous.cidade,
            unidade: isAdm ? previous.unidade : { value: '', label: 'Todas' }
        }));
    };

    function getMonthlabel(monthValue) {
        const monthIndex = parseInt(monthValue, 10) - 1;
        return monthNames[monthIndex] || '';
    }

    const onFilterChange = useCallback((name, value) => {
        if (name === 'cidade' && isAdm && value?.value) {
            localStorage.setItem('dashboard_city', JSON.stringify(value));
        }

        if (name === 'unidade' && value?.value) {
            localStorage.setItem('dashboard_unity', JSON.stringify(value));
        }

        setFilters((previous) => {
            const nextFilters = {
                ...previous,
                [name]: value,
                ...(name === 'cidade' ? { unidade: { value: '', label: 'Todas' } } : {})
            };

            if (name === 'year' && value?.value === '') {
                nextFilters.month = { value: previous.month?.value || currentMonth, label: getMonthlabel(previous.month?.value || currentMonth) };
            }

            if (name === 'month' && value?.value === '') {
                nextFilters.year = { value: previous.year?.value || currentYear, label: previous.year?.value || currentYear };
            }

            const isYearAllSelected = nextFilters.year?.value === '';
            const isMonthAllSelected = nextFilters.month?.value === '';

            if (isYearAllSelected && isMonthAllSelected) {
                return previous;
            }

            return nextFilters;
        });
    }, [isAdm]);

    return (
        <CIHAFiltersContext.Provider
            value={{
                filters: resolvedFilters,
                handleChangeFilters,
                resetFilters,
                onFilterChange,
                isAdm,
                cities: isAdm ? (cities || []) : [],
                unities: unities || []
            }}
        >
            {children}
        </CIHAFiltersContext.Provider>
    );
};

export default CIHAFiltersProvider;
